import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDefaultHeader } from "../utils/httpClient";
import { userLogout } from "../reduxStore/action/authActions";
import strings from "translate/language";
import { handleDetailResponse } from "utils/utils";
import ProfileAvatar from "component/common/ProfileAvatar";
import {
  hydrateJustoSidebarPreference,
  isJustoSidebarCollapsed,
  setJustoSidebarCollapsed,
  toggleJustoSidebar,
} from "utils/justoSidebar";
import "./Header.css";

const Header = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDetail, setProfileDetail] = useState({});
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const dispatch = useDispatch();

  const loginSelector = useSelector((state) => state.login);

  useEffect(() => {
    checklogin();
  }, [loginSelector]);
  useEffect(() => {
    const syncCollapsed = () => setSidebarCollapsed(isJustoSidebarCollapsed());
    hydrateJustoSidebarPreference();
    syncCollapsed();
    const onToggle = () => syncCollapsed();
    window.addEventListener("justo-sidebar-toggle", onToggle);
    const mq = window.matchMedia("(max-width: 991.98px)");
    const handleSideNav = (event) => {
      const isMobile = typeof event.matches === "boolean" ? event.matches : mq.matches;
      if (isMobile) {
        setJustoSidebarCollapsed(true);
      } else {
        hydrateJustoSidebarPreference();
      }
      syncCollapsed();
    };
    if (mq.addEventListener) mq.addEventListener("change", handleSideNav);
    else mq.addListener(handleSideNav);
    return () => {
      window.removeEventListener("justo-sidebar-toggle", onToggle);
      if (mq.removeEventListener) mq.removeEventListener("change", handleSideNav);
      else mq.removeListener(handleSideNav);
    };
  }, []);

  const checklogin = async () => {
    if (loginSelector.response && loginSelector.authToken) {
      if (loginSelector.response.status === 200) {
        await setDefaultHeader("token", loginSelector.response.token);
        var path = window.location.pathname + window.location.search;
        navigate(path ? path : "/dashboard");
      }
    } else {
      navigate("/");
    }
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    dispatch(userLogout());
  };

  const openLogoutModal = () => {
    setShowLogoutModal(true);
    setJustoSidebarCollapsed(false);
    setSidebarCollapsed(false);
  };

  useEffect(() => {
    if (!showLogoutModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowLogoutModal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showLogoutModal]);

  const openclose = () => {
    const next = toggleJustoSidebar();
    setSidebarCollapsed(next);
  };
  const userProfileDetailSelector = useSelector(
    (state) => state?.userProfileUpdate,
  );

  useEffect(() => {
    if (userProfileDetailSelector.response) {
      const { response } = userProfileDetailSelector;
      if (response.status === 200) {
        const data = handleDetailResponse(response?.data);
        let baseUrl = data?.cp_id ? data?.profile_base_url : data?.base_url;
        setProfileDetail({ ...data, base_url: baseUrl });
      } else {
        setProfileDetail({});
      }
    } else {
      if (loginSelector.response && loginSelector.authToken) {
        if (loginSelector.response.status === 200) {
          setProfileDetail(handleDetailResponse(loginSelector?.response?.data));
        } else {
          setProfileDetail({});
        }
      }
    }
  }, [userProfileDetailSelector]);

  const displayName = useMemo(() => {
    const fullName = `${profileDetail?.first_name || ""} ${
      profileDetail?.last_name || ""
    }`.trim();
    return (
      fullName ||
      profileDetail?.user_name ||
      profileDetail?.name ||
      profileDetail?.email ||
      strings.profile
    );
  }, [profileDetail]);

  const roleLabel = useMemo(() => {
    return (
      profileDetail?.role_title ||
      profileDetail?.role_name ||
      profileDetail?.designation ||
      "Account"
    );
  }, [profileDetail]);

  return (
    <div className="page-main-header">
      <div className="main-header-right row">
        <div className="main-header-left d-lg-none">
          <div className="logo-wrapper">
            <a href="/dashboard">
              <img src="/assets/images/JUSTOLOGO.jpg" alt="" />
            </a>
          </div>
        </div>
        <div className="justo-header-toggle-wrap">
          <button
            type="button"
            className="justo-header-toggle"
            aria-label={
              sidebarCollapsed ? "Show navigation" : "Hide navigation"
            }
            aria-pressed={!sidebarCollapsed}
            aria-controls="justo-main-sidebar"
            title={sidebarCollapsed ? "Show navigation" : "Hide navigation"}
            onClick={openclose}
          >
            <i
              className={`fa ${sidebarCollapsed ? "fa-bars" : "fa-times"}`}
              aria-hidden="true"
            />
          </button>
        </div>
        <div className="nav-right col p-0">
          <ul className="nav-menus">
            <li></li>
            <li className="onhover-dropdown">
              <button
                type="button"
                className="header-account"
                aria-haspopup="true"
                aria-label={strings.profile}
              >
                <span className="header-account__avatar-wrap">
                  <ProfileAvatar
                    variant="header"
                    className="header-account__avatar"
                    baseUrl={profileDetail?.base_url}
                    profilePicture={profileDetail?.profile_picture}
                  />
                </span>
                <span className="header-account__meta">
                  <span className="header-account__name">{displayName}</span>
                  <span className="header-account__hint">{roleLabel}</span>
                </span>
                <i
                  className="fa fa-angle-down header-account__chevron"
                  aria-hidden="true"
                />
              </button>
              <ul className="profile-dropdown onhover-show-div header-account-menu">
                <li>
                  <button
                    type="button"
                    className="header-account-menu__item"
                    onClick={() => navigatpage(`/viewuserprofile`)}
                  >
                    <i className="fa fa-user" aria-hidden="true" />
                    <span>{strings.profile}</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="header-account-menu__item"
                    onClick={() => navigatpage(`/changepassword`)}
                  >
                    <i className="fa fa-key" aria-hidden="true" />
                    <span>{strings.changePassword}</span>
                  </button>
                </li>
                <li className="header-account-menu__logout">
                  <button
                    type="button"
                    className="header-account-menu__item header-account-menu__item--danger"
                    onClick={openLogoutModal}
                  >
                    <i className="fa fa-sign-out" aria-hidden="true" />
                    <span>{strings.logout}</span>
                  </button>
                </li>
              </ul>
            </li>
          </ul>
          <div className="d-lg-none mobile-toggle pull-right">
            <i data-feather="more-horizontal" />
          </div>
        </div>
      </div>

      {showLogoutModal
        ? createPortal(
            <div
              className="header-logout-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="header-logout-title"
              onClick={() => setShowLogoutModal(false)}
            >
              <div
                className="header-logout-modal__dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="header-logout-modal__body">
                  <h5
                    className="header-logout-modal__title"
                    id="header-logout-title"
                  >
                    {strings.logoutConfirmTitle}
                  </h5>
                  <p className="header-logout-modal__text">
                    {strings.logoutConfirmMessage}
                  </p>
                </div>
                <div className="header-logout-modal__footer">
                  <button
                    type="button"
                    className="header-logout-modal__btn header-logout-modal__btn--ghost"
                    onClick={() => setShowLogoutModal(false)}
                  >
                    {strings.cancel}
                  </button>
                  <button
                    type="button"
                    className="header-logout-modal__btn header-logout-modal__btn--danger"
                    onClick={confirmLogout}
                  >
                    {strings.logout}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default Header;
